import { useState } from 'react';
import { ThumbsUp, ThumbsDown } from 'lucide-react';

interface VoteValue {
  upvotes: number;
  downvotes: number;
  userVote?: 'up' | 'down' | null;
  voters?: string[]; // IDs dos usuários que votaram
}

interface VoteCellProps {
  value: VoteValue | number | null;
  onChange: (value: VoteValue) => void;
  readOnly?: boolean;
  userId?: string; // ID do usuário atual
}

export function VoteCell({ value, onChange, readOnly, userId = 'user1' }: VoteCellProps) {
  let voteData: VoteValue;

  if (typeof value === 'number') {
    // Compatibilidade: número simples = total de votos
    voteData = { upvotes: value, downvotes: 0, userVote: null, voters: [] };
  } else if (value) {
    voteData = value;
  } else {
    voteData = { upvotes: 0, downvotes: 0, userVote: null, voters: [] };
  }

  const totalVotes = voteData.upvotes - voteData.downvotes;
  const hasVoted = voteData.voters?.includes(userId);

  const handleVote = (type: 'up' | 'down') => {
    if (readOnly || hasVoted) return;

    const newVoteData = { ...voteData };
    
    if (type === 'up') {
      newVoteData.upvotes += 1;
      newVoteData.userVote = 'up';
    } else {
      newVoteData.downvotes += 1;
      newVoteData.userVote = 'down';
    }
    
    newVoteData.voters = [...(newVoteData.voters || []), userId];
    onChange(newVoteData);
  };

  const getScoreColor = () => {
    if (totalVotes > 0) return 'text-green-600';
    if (totalVotes < 0) return 'text-red-600';
    return 'text-gray-600';
  };

  if (readOnly) {
    return (
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 text-gray-500">
          <ThumbsUp className="w-4 h-4" />
          <span className="text-sm">{voteData.upvotes}</span>
        </div>
        <div className="flex items-center gap-1 text-gray-500">
          <ThumbsDown className="w-4 h-4" />
          <span className="text-sm">{voteData.downvotes}</span>
        </div>
        <span className={`text-sm font-semibold ${getScoreColor()}`}>
          {totalVotes > 0 ? '+' : ''}{totalVotes}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={() => handleVote('up')}
        disabled={hasVoted}
        className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
          voteData.userVote === 'up'
            ? 'bg-green-100 text-green-700'
            : hasVoted
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:bg-green-50 hover:text-green-600'
        }`}
      >
        <ThumbsUp className="w-4 h-4" />
        <span className="text-sm font-medium">{voteData.upvotes}</span>
      </button>

      <button
        onClick={() => handleVote('down')}
        disabled={hasVoted}
        className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
          voteData.userVote === 'down'
            ? 'bg-red-100 text-red-700'
            : hasVoted
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
        }`}
      >
        <ThumbsDown className="w-4 h-4" />
        <span className="text-sm font-medium">{voteData.downvotes}</span>
      </button>

      <div className={`text-sm font-bold ${getScoreColor()}`}>
        {totalVotes > 0 ? '+' : ''}{totalVotes}
      </div>
    </div>
  );
}
